import React, { useRef, useEffect, useCallback } from 'react';

const TARGET_W = 400;
const TARGET_H = 150;
const RULER_SIZE = 24;
const TICK_INTERVAL = 50; // pixels between ruler ticks

interface Props {
  image: HTMLImageElement | null;
  zoom: number;
  rotation: number;
  flipH: boolean;
  flipV: boolean;
  offset: { x: number; y: number };
  onOffsetChange: (offset: { x: number; y: number }) => void;
}

const SignatureCanvas: React.FC<Props> = ({
  image, zoom, rotation, flipH, flipV, offset, onOffsetChange,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDragging = useRef(false);
  const lastMouse = useRef({ x: 0, y: 0 });

  const canvasW = TARGET_W + RULER_SIZE * 2;
  const canvasH = TARGET_H + RULER_SIZE * 2;

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvasW, canvasH);

    // Checkerboard background for target area (transparency indicator)
    const tileSize = 10;
    for (let y = RULER_SIZE; y < RULER_SIZE + TARGET_H; y += tileSize) {
      for (let x = RULER_SIZE; x < RULER_SIZE + TARGET_W; x += tileSize) {
        const col = Math.floor((x - RULER_SIZE) / tileSize);
        const row = Math.floor((y - RULER_SIZE) / tileSize);
        ctx.fillStyle = (col + row) % 2 === 0 ? '#ffffff' : '#e5e5e5';
        ctx.fillRect(x, y, tileSize, tileSize);
      }
    }

    // Draw image in target area (clipped)
    if (image) {
      ctx.save();
      ctx.beginPath();
      ctx.rect(RULER_SIZE, RULER_SIZE, TARGET_W, TARGET_H);
      ctx.clip();

      const cx = RULER_SIZE + TARGET_W / 2 + offset.x;
      const cy = RULER_SIZE + TARGET_H / 2 + offset.y;
      ctx.translate(cx, cy);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.scale(flipH ? -zoom : zoom, flipV ? -zoom : zoom);
      ctx.drawImage(image, -image.width / 2, -image.height / 2);
      ctx.restore();
    }

    // Dim area outside target
    ctx.fillStyle = 'rgba(0,0,0,0.5)';
    ctx.fillRect(0, 0, canvasW, RULER_SIZE); // top
    ctx.fillRect(0, RULER_SIZE + TARGET_H, canvasW, RULER_SIZE); // bottom
    ctx.fillRect(0, RULER_SIZE, RULER_SIZE, TARGET_H); // left
    ctx.fillRect(RULER_SIZE + TARGET_W, RULER_SIZE, RULER_SIZE, TARGET_H); // right

    // Draw rulers
    ctx.fillStyle = '#f1f5f9';
    ctx.fillRect(RULER_SIZE, 0, TARGET_W, RULER_SIZE); // top ruler bg
    ctx.fillRect(0, RULER_SIZE, RULER_SIZE, TARGET_H); // left ruler bg

    ctx.fillStyle = '#64748b';
    ctx.font = '9px monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';

    // Top ruler ticks
    for (let px = 0; px <= TARGET_W; px += TICK_INTERVAL) {
      const x = RULER_SIZE + px;
      const isMajor = px % 100 === 0;
      ctx.beginPath();
      ctx.moveTo(x, RULER_SIZE - (isMajor ? 12 : 6));
      ctx.lineTo(x, RULER_SIZE);
      ctx.strokeStyle = '#94a3b8';
      ctx.lineWidth = isMajor ? 1 : 0.5;
      ctx.stroke();
      if (isMajor) ctx.fillText(`${px}`, x, 2);
    }

    // Left ruler ticks
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    for (let py = 0; py <= TARGET_H; py += TICK_INTERVAL) {
      const y = RULER_SIZE + py;
      const isMajor = py % 100 === 0;
      ctx.beginPath();
      ctx.moveTo(RULER_SIZE - (isMajor ? 12 : 6), y);
      ctx.lineTo(RULER_SIZE, y);
      ctx.strokeStyle = '#94a3b8';
      ctx.lineWidth = isMajor ? 1 : 0.5;
      ctx.stroke();
      if (isMajor) ctx.fillText(`${py}`, RULER_SIZE - 14, y);
    }

    // Target border
    ctx.strokeStyle = '#6366f1';
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 3]);
    ctx.strokeRect(RULER_SIZE, RULER_SIZE, TARGET_W, TARGET_H);
    ctx.setLineDash([]);

    // Dimension label
    ctx.fillStyle = '#6366f1';
    ctx.font = 'bold 11px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(`${TARGET_W} × ${TARGET_H} px`, RULER_SIZE + TARGET_W / 2, RULER_SIZE + TARGET_H + 4);
  }, [image, zoom, rotation, flipH, flipV, offset, canvasW, canvasH]);

  useEffect(() => { draw(); }, [draw]);

  const handleMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true;
    lastMouse.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current) return;
    const dx = e.clientX - lastMouse.current.x;
    const dy = e.clientY - lastMouse.current.y;
    lastMouse.current = { x: e.clientX, y: e.clientY };
    onOffsetChange({ x: offset.x + dx, y: offset.y + dy });
  };

  const handleMouseUp = () => { isDragging.current = false; };

  return (
    <canvas
      ref={canvasRef}
      width={canvasW}
      height={canvasH}
      className="border border-slate-300 rounded-lg cursor-grab active:cursor-grabbing bg-slate-100"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    />
  );
};

export { TARGET_W, TARGET_H, RULER_SIZE };
export default SignatureCanvas;
